/**
 * Where a monthly check-in email lands. It records the answer only when the customer presses
 * Confirm, because mail scanners fetch every link with GET (MSG-045). New labs and a health
 * change then open the account at the right page. No changes stays here.
 */
import { useEffect, useState } from "react";
import type { CheckInAnswer } from "@lattice/core";
import { isCheckInAnswer } from "@lattice/core";
import { Button } from "@lattice/ui/Button";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Status } from "@lattice/ui/Status";
import { answerCheckIn } from "./api.ts";
import { AuthLayout } from "./AuthLayout.tsx";
import { SUPPORT_ADDRESS } from "./PublicPage.tsx";
import { navigate, usePath } from "./router.ts";

const PREFIX = "/check-in/";

const SIGNATURE = /^[0-9a-f]{64}$/;

const COPY = {
  no_changes: {
    heading: "Confirm no changes this month",
    body: "Your next pouch is made from your current formula and your physician is told nothing changed. If you do not answer, it is made the same way.",
    button: "Confirm no changes",
  },
  new_labs: {
    heading: "Upload your new labs",
    body: "Continue to your account to upload them. Until you do, your next pouch uses your current formula.",
    button: "Upload new labs",
  },
  health_change: {
    heading: "Update your health answers",
    body: "Continue to your account to update your health answers. A new medication, condition or pregnancy goes to your physician before your next pouch is made.",
    button: "Update health answers",
  },
  done: {
    heading: "You're all set",
    body: "Your formula renews as it is.",
    button: "",
  },
  offline: {
    heading: "We could not save that",
    body: "Check your connection and try again. Nothing was changed.",
    button: "Try again",
  },
  failed: {
    heading: "This link did not work",
    body: `This link has expired, was already used or was cut off. Sign in to reach your account, or write to ${SUPPORT_ADDRESS} and we will help.`,
    button: "",
  },
} as const;

type Phase = "ask" | "pending" | "done" | "failed" | "offline";

function wording(phase: Phase, answer: CheckInAnswer | null): (typeof COPY)[keyof typeof COPY] {
  if (phase === "done") return COPY.done;

  if (phase === "offline") return COPY.offline;

  if (phase === "failed" || answer === null) return COPY.failed;

  return COPY[answer];
}

function tokenFrom(
  path: string,
): { readonly token: string; readonly answer: CheckInAnswer } | null {
  if (!path.startsWith(PREFIX)) return null;
  const token = path.slice(PREFIX.length);

  if (token === "" || token.includes("/")) return null;
  const [id, answer, signature, ...rest] = token.split(".");

  if (!id || !answer || !signature || rest.length > 0) return null;

  if (!isCheckInAnswer(answer) || !SIGNATURE.test(signature)) return null;

  return { token, answer };
}

function Action({
  phase,
  answer,
  label,
  onConfirm,
}: {
  readonly phase: Phase;
  readonly answer: CheckInAnswer | null;
  readonly label: string;
  readonly onConfirm: () => void;
}) {
  if (phase === "done")
    return (
      <>
        <Status state="ok">Saved. You can close this page.</Status>
        <LinkButton href="/account" tone="quiet">
          Go to your account
        </LinkButton>
      </>
    );

  if (phase === "failed")
    return (
      <>
        <LinkButton href="/sign-in">Sign in</LinkButton>
        <LinkButton href={`mailto:${SUPPORT_ADDRESS}`} tone="quiet">
          Email support
        </LinkButton>
      </>
    );

  return (
    <>
      <Button testId="confirm-check-in" wide loading={phase === "pending"} onClick={onConfirm}>
        {label}
      </Button>
      {phase === "ask" && answer === "no_changes" ? (
        <LinkButton href="/account" tone="quiet">
          Report a change
        </LinkButton>
      ) : null}
    </>
  );
}

export function CheckIn() {
  const parsed = tokenFrom(usePath());
  const [phase, setPhase] = useState<Phase>(parsed === null ? "failed" : "ask");

  useEffect(() => {
    document.title = "Monthly check-in · Lattice";
  }, []);

  const copy = wording(phase, parsed?.answer ?? null);

  const confirm = () => {
    if (parsed === null || (phase !== "ask" && phase !== "offline")) return;
    setPhase("pending");
    answerCheckIn(parsed.token)
      .then((result) => {
        if (result === null) {
          setPhase("failed");

          return;
        }

        if (result.next !== null) navigate(result.next);
        else setPhase("done");
      })
      .catch(() => setPhase("offline"));
  };

  return (
    <AuthLayout
      title={copy.heading}
      lead={copy.body}
      focusHeading={phase === "done" || phase === "failed" || phase === "offline"}
    >
      <Action
        phase={phase}
        answer={parsed?.answer ?? null}
        label={copy.button}
        onConfirm={confirm}
      />
    </AuthLayout>
  );
}
